import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

/**
 * App-wide error boundary. Catches uncaught React render / lifecycle errors AND
 * an "unhandledrejection" listener catches any awaited API call that wasn't
 * `.catch()`ed. Prevents the raw CRA dev overlay ("AxiosError: Request failed
 * with status code 404") from showing to ERP users.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
    this._onUnhandled = this._onUnhandled.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || String(error) };
  }

  componentDidCatch(error, info) {
    // Log for diagnostics — do not swallow silently.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  componentDidMount() {
    window.addEventListener("unhandledrejection", this._onUnhandled);
  }
  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this._onUnhandled);
  }

  _onUnhandled(ev) {
    // Suppress raw axios errors from bubbling up to CRA's overlay.
    // The individual API call sites should handle their own errors — but if
    // one is missed, log it to console (for diagnostics) and prevent the overlay.
    const err = ev.reason;
    if (err && (err.isAxiosError || err.name === "AxiosError")) {
      // eslint-disable-next-line no-console
      console.warn("[unhandled axios]", err.config?.url, err.response?.status, err.message);
      ev.preventDefault();
    }
  }

  reset = () => {
    this.setState({ hasError: false, message: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="app-error-boundary">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-md shadow-sm p-8">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-700" />
          </div>
          <h1 className="mt-4 text-lg font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            We hit an unexpected error while rendering this page. Your data is safe.
            Please refresh the page or return to Reports.
          </p>
          {this.state.message && (
            <details className="mt-3 text-[11px] text-slate-500 font-mono">
              <summary className="cursor-pointer">Technical details (for support)</summary>
              <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded overflow-auto">
                {this.state.message}
              </div>
            </details>
          )}
          <div className="mt-6 flex gap-2">
            <Button onClick={() => window.location.reload()} data-testid="error-reload" className="bg-blue-900 hover:bg-blue-800 text-white">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reload
            </Button>
            <Link to="/reports">
              <Button variant="outline" onClick={this.reset} data-testid="error-home">
                <Home className="w-3.5 h-3.5 mr-1.5" /> Back to Reports
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
